// The bulk-action dialogs for the desktop address book, plus the household
// manager. Split out of contacts.ts purely for size: that file is the SCREEN --
// filters, rows, selection, detail -- and these are the modal workflows it
// launches. Nothing here renders unless something is selected.
//
// SocialTie's own address book does seven actions on a selection. Four are
// built here (Manage Social Ties, Add to Contact Lists, Create Household,
// Delete) and three are deliberately absent:
//
//   * Send Message  -- MySocialCal has no contact-to-contact messaging at all.
//                      Chat is scoped to an event. There is nowhere to send to,
//                      so this is a feature to design, not a button to add.
//   * Send Email    -- mass mail to hundreds of real people. That is a
//                      deliverability, CAN-SPAM and sender-reputation decision
//                      before it is a UI, and the CSV export already feeds a
//                      real mail-merge tool that handles unsubscribes. Will's
//                      call, not a default.
//   * Invite to Event -- the address book is USER-scoped and events are
//                      GROUP-scoped, so this needs a group and event picker
//                      that does not exist here yet, and it collides with the
//                      rule in docs/domain.md that an SMS invite to a
//                      brand-new person is refused. A feature of its own.
//
// See docs/plan-address-book.md and the migration-plan handoff.

import {
  ApiClientError,
  bulkDeleteContacts,
  bulkSetList,
  bulkSetTies,
  createHousehold,
  deleteHousehold,
  listHouseholds,
  renameHousehold,
  setHouseholdMembers,
} from '../api/client.js';
import { el } from '../dom.js';
import {
  SOCIAL_TIES,
  socialTieLabel,
  type BulkResultDto,
  type BulkTieMode,
  type ContactListDto,
  type HouseholdDto,
  type SocialTie,
} from '@msc/shared';
import { modal, type ModalHandle } from './modal.js';

/**
 * What every dialog here needs from the screen that opened it.
 *
 * `selected` is passed as a plain array snapshot rather than the live Set: a
 * dialog that read the Set would act on whatever was selected when the user hit
 * Apply, which is not what the dialog's own heading said when it opened.
 */
export interface BulkContext {
  selected: number[];
  lists: ContactListDto[];
  /** Re-fetch the contact rows. */
  refresh: () => Promise<void>;
  /** Re-fetch the lists, so member counts in the rail stay honest. */
  reloadLists: () => Promise<void>;
  /** Drop the selection. Called after an action that invalidates it. */
  clearSelection: () => void;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Report what actually happened, and say so when it is not what was asked.
 *
 * `affected` and `requested` diverge when a selection has gone stale -- another
 * tab deleted a contact, or a row was filtered away between select and apply.
 * Reporting only the request count would turn a partial no-op into a confident
 * success, which is the failure mode a bulk tool can least afford.
 */
function resultText(r: BulkResultDto, verb: string): string {
  if (r.affected === r.requested) return `${verb} ${plural(r.affected, 'contact', 'contacts')}.`;
  return (
    `${verb} ${plural(r.affected, 'contact', 'contacts')}. ` +
    `${r.requested - r.affected} were no longer available -- your selection may be out of date.`
  );
}

/**
 * A modal that destroys itself on EVERY close route, not just the happy one.
 *
 * modal()'s own destroy() removes the overlay but leaves `body.modal-open` set,
 * so it has to run from inside onClose, which fires after close() has already
 * cleared that state. Getting this backwards leaves the page scroll-locked with
 * nothing on screen to explain it.
 */
function selfClosingModal(title: string, body: HTMLElement): ModalHandle {
  const dlg: ModalHandle = modal({ title, body, onClose: () => dlg.destroy() });
  return dlg;
}

/**
 * A message this module wrote and MEANS to show the user.
 *
 * `wire()` needs to tell three kinds of failure apart. An ApiClientError
 * carries the server's own wording and is safe to show. A ValidationError is
 * ours, thrown before the request goes out ("tick at least one tie"), and is
 * the whole point of validating client-side. Anything else is a bug -- a
 * TypeError from a bad assumption in this file -- and its message is developer
 * text that must not reach a user.
 *
 * The first version threw a plain Error for the second case, which is
 * indistinguishable from the third, so every one of these messages was replaced
 * by "That did not work" and the user was never told what to fix.
 */
class ValidationError extends Error {}

/** The shared shape: an error line, a submit button, and a form that owns both. */
interface FormParts {
  form: HTMLFormElement;
  error: HTMLParagraphElement;
  submit: HTMLButtonElement;
}

function formShell(
  submitLabel: string,
  children: (Node | string)[],
  danger = false,
): FormParts {
  const error = el('p', { class: 'form-error' }, '') as HTMLParagraphElement;
  error.hidden = true;
  const submit = el(
    'button',
    { type: 'submit', class: danger ? 'btn btn-danger' : 'btn' },
    submitLabel,
  ) as HTMLButtonElement;
  // method="post" even though JS submits: if the script fails, the browser must
  // not fall back to GET and put the payload in the URL.
  const form = el('form', { method: 'post', class: 'bulk-form' }, [
    ...children,
    error,
    el('div', { class: 'form-actions' }, [submit]),
  ]) as HTMLFormElement;
  return { form, error, submit };
}

/** Run the action, report it, and leave the dialog open on failure. */
function wire(
  parts: FormParts,
  dlg: ModalHandle,
  ctx: BulkContext,
  run: () => Promise<string>,
  opts: { clearSelection?: boolean } = {},
): void {
  parts.form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    parts.error.hidden = true;
    parts.submit.setAttribute('disabled', 'disabled');
    void run()
      .then(async (message) => {
        dlg.close();
        if (opts.clearSelection === true) ctx.clearSelection();
        await ctx.refresh();
        // window.alert rather than a toast: the SPA has no toast system, and a
        // bulk write that silently redraws the same table is indistinguishable
        // from a button that did nothing.
        window.alert(message);
      })
      .catch((err: unknown) => {
        parts.error.textContent =
          err instanceof ApiClientError || err instanceof ValidationError
            ? err.message
            : 'That did not work. Nothing was changed.';
        parts.error.hidden = false;
        parts.submit.removeAttribute('disabled');
      });
  });
}

// --- Manage social ties ----------------------------------------------------

/**
 * Add, remove or replace ties across the selection.
 *
 * Three modes, because "apply Family to these 40" is genuinely ambiguous and
 * SocialTie's own dialog separates them. `replace` is destructive in a way the
 * other two are not -- it discards ties the user never looked at, on contacts
 * they selected for some other reason -- so it carries its own warning and the
 * button changes wording to match.
 */
export function openBulkTies(ctx: BulkContext): void {
  const boxes = new Map<SocialTie, HTMLInputElement>();
  const tieChecks = SOCIAL_TIES.map((tie) => {
    const box = el('input', { type: 'checkbox', value: tie }) as HTMLInputElement;
    boxes.set(tie, box);
    return el('label', { class: 'check' }, [box, ` ${socialTieLabel(tie)}`]);
  });

  const mode = el('select', { id: 'bt-mode' }) as HTMLSelectElement;
  mode.append(
    el('option', { value: 'add' }, 'Add these ties'),
    el('option', { value: 'remove' }, 'Remove these ties'),
    el('option', { value: 'replace' }, 'Replace all ties with these'),
  );

  const note = el('p', { class: 'muted small' }, '');
  const parts = formShell('Apply', [
    el('p', {}, `${plural(ctx.selected.length, 'contact', 'contacts')} selected.`),
    el('div', { class: 'field' }, [el('label', { for: 'bt-mode' }, 'What to do'), mode]),
    el('div', { class: 'check-list' }, tieChecks),
    note,
  ]);

  function syncMode(): void {
    const replacing = mode.value === 'replace';
    note.textContent = replacing
      ? 'Every tie these contacts already have will be removed first, including ties not shown as ticked here. With nothing ticked, this clears all their ties.'
      : mode.value === 'remove'
        ? 'Only the ticked ties are removed. Anything else they hold is left alone.'
        : 'The ticked ties are added. Ties they already hold are left alone.';
    parts.submit.classList.toggle('btn-danger', replacing);
    parts.submit.textContent = replacing ? 'Replace ties' : 'Apply';
  }
  mode.addEventListener('change', syncMode);
  syncMode();

  const dlg = selfClosingModal('Manage social ties', parts.form);
  dlg.open();

  wire(parts, dlg, ctx, async () => {
    const ties = SOCIAL_TIES.filter((t) => boxes.get(t)?.checked === true);
    const chosen = mode.value === 'remove' || mode.value === 'add' || mode.value === 'replace'
      ? (mode.value as BulkTieMode)
      : 'add';
    if (ties.length === 0 && chosen !== 'replace') {
      throw new ValidationError('Tick at least one tie, or switch to Replace to clear them all.');
    }
    const r = await bulkSetTies(ctx.selected, ties, chosen);
    return resultText(r, 'Updated');
  });
}

// --- Add to contact lists --------------------------------------------------

/**
 * One list at a time, and a direction.
 *
 * Not a grid of every list with tri-state checkboxes: with 19 lists and 40
 * selected contacts, a checkbox that is neither on nor off for the selection
 * has to mean "leave alone", and users read it as "off". One list and an
 * explicit Add or Remove cannot be misread.
 */
export function openBulkList(ctx: BulkContext): void {
  const pick = el('select', { id: 'bl-list' }) as HTMLSelectElement;
  for (const l of ctx.lists) {
    pick.append(el('option', { value: String(l.listId) }, `${l.name} (${l.memberCount})`));
  }
  const action = el('select', { id: 'bl-action' }) as HTMLSelectElement;
  action.append(
    el('option', { value: 'add' }, 'Add to this list'),
    el('option', { value: 'remove' }, 'Remove from this list'),
  );

  const parts = formShell('Apply', [
    el('p', {}, `${plural(ctx.selected.length, 'contact', 'contacts')} selected.`),
    el('div', { class: 'field' }, [el('label', { for: 'bl-list' }, 'List'), pick]),
    el('div', { class: 'field' }, [el('label', { for: 'bl-action' }, 'What to do'), action]),
  ]);

  const dlg = selfClosingModal('Add to contact lists', parts.form);
  dlg.open();

  wire(parts, dlg, ctx, async () => {
    const listId = Number(pick.value);
    if (!Number.isInteger(listId) || listId <= 0) throw new ValidationError('Choose a list.');
    const r = await bulkSetList(ctx.selected, listId, action.value === 'add');
    // The rail shows member counts, so they have to be re-read or the number
    // beside the list you just changed is the one thing on screen still wrong.
    await ctx.reloadLists();
    return resultText(r, action.value === 'add' ? 'Added' : 'Removed');
  });
}

// --- Create household ------------------------------------------------------

/**
 * A household is an ENVELOPE, not a family: it exists so the Christmas card run
 * sends the Smiths one card instead of four. It is NOT the family tree, which
 * is a graph of blood and affinity relationships with entirely different rules
 * and is not built. Saying so here matters, because "household" is exactly the
 * word someone reaches for when they mean the tree.
 *
 * The name is pre-filled from the most common surname in the selection, because
 * that is right almost every time and wrong in a way that is obvious on sight.
 */
export function openCreateHousehold(ctx: BulkContext, suggested: string): void {
  const name = el('input', {
    type: 'text',
    id: 'hh-name',
    required: true,
    maxlength: '191',
    autocapitalize: 'words',
  }) as HTMLInputElement;
  name.value = suggested;

  const parts = formShell('Create household', [
    el('p', {}, `${plural(ctx.selected.length, 'contact', 'contacts')} will be grouped.`),
    el('div', { class: 'field' }, [el('label', { for: 'hh-name' }, 'Household name'), name]),
    el(
      'p',
      { class: 'muted small' },
      'A household groups people at one address so a mail merge sends them a single card. It is not the family tree.',
    ),
  ]);

  const dlg = selfClosingModal('Create household', parts.form);
  dlg.open();
  name.focus();
  name.select();

  wire(parts, dlg, ctx, async () => {
    const trimmed = name.value.trim();
    if (trimmed === '') throw new ValidationError('Give the household a name.');
    const r = await createHousehold(trimmed, ctx.selected);
    return `Created "${trimmed}" with ${plural(r.moved, 'contact', 'contacts')}.`;
  });
}

// --- Delete ----------------------------------------------------------------

/**
 * Typed confirmation, not an OK button.
 *
 * A stray Enter on a native confirm() can erase 400 contacts, and this is a
 * soft delete with no UI anywhere to undo it. Making the user type the count is
 * the cheapest thing that forces them to read the number they are about to act
 * on. Reserved for THIS action: the other three are all reversible by hand.
 */
export function openBulkDelete(ctx: BulkContext): void {
  const n = ctx.selected.length;
  const confirmBox = el('input', {
    type: 'text',
    id: 'bd-confirm',
    required: true,
    autocomplete: 'off',
    inputmode: 'numeric',
  }) as HTMLInputElement;

  const parts = formShell(
    `Delete ${plural(n, 'contact', 'contacts')}`,
    [
      el('p', {}, `You are about to delete ${plural(n, 'contact', 'contacts')}.`),
      el(
        'p',
        { class: 'muted small' },
        'They are removed from every list and household. There is no undo in the app.',
      ),
      el('div', { class: 'field' }, [
        el('label', { for: 'bd-confirm' }, `Type ${n} to confirm`),
        confirmBox,
      ]),
    ],
    true,
  );

  const dlg = selfClosingModal('Delete contacts', parts.form);
  dlg.open();
  confirmBox.focus();

  wire(
    parts,
    dlg,
    ctx,
    async () => {
      if (confirmBox.value.trim() !== String(n)) {
        throw new ValidationError(`Type ${n} exactly, to confirm you mean all of them.`);
      }
      const r = await bulkDeleteContacts(ctx.selected);
      return resultText(r, 'Deleted');
    },
    // The rows are gone, so keeping them selected would leave the action bar
    // offering to act on contacts that no longer exist.
    { clearSelection: true },
  );
}

// --- The household manager -------------------------------------------------

/**
 * View, rename and dissolve households, and move a selection into an existing
 * one. The only place `households` has ever had a UI.
 *
 * Dissolving RELEASES the members rather than deleting them, and the wording
 * says so everywhere: the word "delete" on a row that lists four people's names
 * reads as a threat to the people.
 */
export function openHouseholds(ctx: BulkContext): void {
  const body = el('div', { class: 'household-manager' }, [
    el('p', { class: 'muted' }, 'Loading'),
  ]);
  const dlg = selfClosingModal('Households', body);
  dlg.open();

  async function render(): Promise<void> {
    let households: HouseholdDto[];
    try {
      households = (await listHouseholds()).households;
    } catch {
      body.replaceChildren(el('p', { class: 'form-error' }, 'Could not load households.'));
      return;
    }

    const intro = el(
      'p',
      { class: 'muted small' },
      'A household groups people at one address so a mail merge sends one card. It is not the family tree.',
    );

    if (households.length === 0) {
      body.replaceChildren(
        intro,
        el(
          'p',
          {},
          ctx.selected.length > 0
            ? 'No households yet. Select contacts and use Create household to make the first one.'
            : 'No households yet. Select some contacts on the list first, then use Create household.',
        ),
      );
      return;
    }

    const rows = households.map((h) => {
      const nameEl = el('span', { class: 'hh-name' }, h.name);
      const members = el(
        'span',
        { class: 'muted small hh-members' },
        h.memberCount === 0
          ? 'No members'
          : // Truncated in the SUMMARY only, never in storage. Four names is
            // enough to recognise a household; twenty is a wall of text.
            h.memberNames.slice(0, 4).join(', ') +
              (h.memberCount > 4 ? ` and ${h.memberCount - 4} more` : ''),
      );

      const rename = el('button', { type: 'button', class: 'btn btn-link' }, 'Rename');
      rename.addEventListener('click', () => {
        const next = window.prompt('Household name', h.name);
        if (next === null || next.trim() === '' || next.trim() === h.name) return;
        void renameHousehold(h.householdId, next.trim())
          .then(render)
          .catch(() => window.alert('Could not rename that household.'));
      });

      const dissolve = el('button', { type: 'button', class: 'btn btn-link btn-danger' }, 'Dissolve');
      dissolve.addEventListener('click', () => {
        if (
          !window.confirm(
            `Dissolve "${h.name}"? Its ${plural(h.memberCount, 'member', 'members')} stay in your address book; they just stop being grouped.`,
          )
        ) {
          return;
        }
        void deleteHousehold(h.householdId)
          .then(async () => {
            await render();
            await ctx.refresh();
          })
          .catch(() => window.alert('Could not dissolve that household.'));
      });

      const actions = el('div', { class: 'hh-actions' }, [rename, dissolve]);

      // Only offered when there IS a selection, and it says how many: a bare
      // "Move here" button with nothing selected is a control that silently
      // does nothing.
      if (ctx.selected.length > 0) {
        const move = el(
          'button',
          { type: 'button', class: 'btn btn-link' },
          `Move ${ctx.selected.length} here`,
        );
        move.addEventListener('click', () => {
          void setHouseholdMembers(h.householdId, ctx.selected)
            .then(async (r) => {
              await render();
              await ctx.refresh();
              window.alert(resultText(r, 'Moved'));
            })
            .catch(() => window.alert('Could not move those contacts.'));
        });
        actions.prepend(move);
      }

      return el('li', { class: 'hh-row' }, [
        el('div', { class: 'hh-main' }, [nameEl, members]),
        actions,
      ]);
    });

    const children: (Node | string)[] = [intro, el('ul', { class: 'hh-list' }, rows)];

    if (ctx.selected.length > 0) {
      const out = el(
        'button',
        { type: 'button', class: 'btn btn-secondary' },
        `Remove ${plural(ctx.selected.length, 'contact', 'contacts')} from any household`,
      );
      out.addEventListener('click', () => {
        void setHouseholdMembers(null, ctx.selected)
          .then(async (r) => {
            await render();
            await ctx.refresh();
            window.alert(resultText(r, 'Removed from a household:'));
          })
          .catch(() => window.alert('Could not update those contacts.'));
      });
      children.push(el('div', { class: 'form-actions' }, [out]));
    }

    body.replaceChildren(...children);
  }

  void render();
}
